import React from "react";

import {
  ConfigProvider,
  Flex,
  Layout,
  Menu,
  Typography,
} from "antd";

import type {
  AppShellNavItem,
  AppShellSlot,
  AppShellSlotState,
} from "./AppShell.types";
import type { MenuProps } from "antd";


interface AppShellSidebarProps {
  brandIcon: React.ReactNode;
  brandName: React.ReactNode;
  navigationItems: AppShellNavItem[];
  selectedNavigationKey?: string;
  onNavigationSelect?: (key: string) => void;
  sidebarTopContent?: AppShellSlot;
  sidebarFooter?: AppShellSlot;
  expanded: boolean;
  siderWidth: number;
  collapsedWidth: number;
}

const renderBrandName = (brandName: React.ReactNode) => {
  if (brandName === null || brandName === undefined || brandName === false) {
    return null;
  }

  if (React.isValidElement(brandName)) {
    return brandName;
  }

  return (
    <Typography.Text
      style={{
        color: "#FFFFFF",
        fontSize: 20,
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: "nowrap",
      }}
    >
      {brandName}
    </Typography.Text>
  );
};

const renderShellSlot = (
  slot: AppShellSlot | undefined,
  slotState: AppShellSlotState,
) => {
  if (!slot) {
    return null;
  }

  return typeof slot === "function" ? slot(slotState) : slot;
};

export const AppShellSidebar: React.FC<AppShellSidebarProps> = ({
  brandIcon,
  brandName,
  navigationItems,
  selectedNavigationKey,
  onNavigationSelect,
  sidebarTopContent,
  sidebarFooter,
  expanded,
  siderWidth,
  collapsedWidth,
}) => {
  const menuItems: MenuProps["items"] = navigationItems.map((item) => ({
    key: item.key,
    icon: item.icon,
    disabled: item.disabled,
    title: item.title ?? (typeof item.label === "string" ? item.label : undefined),
    label: (
      <Flex vertical gap={0} style={{ minWidth: 0, paddingBlock: 6 }}>
        <Typography.Text
          ellipsis
          style={{
            color: "inherit",
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.2,
          }}
        >
          {item.label}
        </Typography.Text>
        {item.description ? (
          <Typography.Text
            ellipsis
            style={{
              color: "rgba(255, 255, 255, 0.6)",
              fontSize: 10,
              lineHeight: 1.2,
              marginTop: 4,
            }}
          >
            {item.description}
          </Typography.Text>
        ) : null}
      </Flex>
    ),
  }));

  const handleMenuClick: MenuProps["onClick"] = ({ key }) => {
    const selectedItem = navigationItems.find((item) => item.key === key);
    onNavigationSelect?.(key);
    selectedItem?.onClick?.();
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Menu: {
            darkItemBg: "#050607",
            darkSubMenuItemBg: "#050607",
            darkItemSelectedBg: "#2C2411",
            darkItemSelectedColor: "#F4C145",
            darkItemHoverBg: "#151617",
            darkItemHoverColor: "#FFFFFF",
            darkItemColor: "rgba(255, 255, 255, 0.88)",
            itemBorderRadius: 10,
            itemHeight: 60,
            collapsedWidth,
            iconSize: 16,
          },
        },
      }}
    >
      <Layout.Sider
        width={siderWidth}
        collapsedWidth={collapsedWidth}
        collapsed={!expanded}
        trigger={null}
        theme="dark"
        style={{
          background: "#050607",
          borderRight: "1px solid rgba(255, 255, 255, 0.08)",
          position: "sticky",
          top: 0,
          insetInlineStart: 0,
          height: "100vh",
          overflow: "visible",
        }}
      >
        <Flex vertical style={{ height: "100%" }}>
          <Flex
            align="center"
            justify={expanded ? "space-between" : "center"}
            style={{
              minHeight: 56,
              paddingInline: expanded ? 16 : 8,
              borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
              position: "relative",
            }}
          >
            <Flex align="center" gap={12} style={{ overflow: "hidden" }}>
              {brandIcon}
              {expanded ? renderBrandName(brandName) : null}
            </Flex>
          </Flex>

          {sidebarTopContent ? (
            <Flex
              vertical
              style={{
                paddingInline: expanded ? 8 : 6,
                paddingTop: expanded ? 12 : 8,
              }}
            >
              {renderShellSlot(sidebarTopContent, { expanded })}
            </Flex>
          ) : null}

          <Menu
            mode="inline"
            theme="dark"
            inlineCollapsed={!expanded}
            selectedKeys={selectedNavigationKey ? [selectedNavigationKey] : []}
            items={menuItems}
            onClick={handleMenuClick}
            style={{
              background: "transparent",
              borderInlineEnd: "none",
              flex: 1,
              paddingInline: expanded ? 8 : 6,
              paddingTop: expanded ? 16 : 8,
            }}
          />

          {sidebarFooter ? (
            <Flex
              vertical
              style={{
                borderTop: "1px solid rgba(255, 255, 255, 0.08)",
                padding: 8,
              }}
            >
              {renderShellSlot(sidebarFooter, { expanded })}
            </Flex>
          ) : null}
        </Flex>
      </Layout.Sider>
    </ConfigProvider>
  );
};
